<script lang="ts">
import { computed, defineComponent, h, onMounted, onUpdated, reactive, ref, unref } from 'vue';

import { classNames, eagerComputed, getCurrentInstance, pickAttrs } from '@components/utils';
import { useRender } from '@components/composable';

import { CdsCheckbox } from '../CdsCheckbox';
import { CdsIcon } from '../CdsIcon';

import renderIndent from './components/Indent';
import { useInjectKeysState, useInjectTreeContext } from './components/TreeContext';
import { makeTreeNodeProps } from './composable';
import { convertNodePropsToEventData, getTreeNodeProps } from './utils';

import type { NTree } from './interface';
import type { VNode, CSSProperties } from 'vue';

const ICON_OPEN = 'open';
const ICON_CLOSE = 'close';

const defaultTitle = '---';
const prefixCls = 'cds-treenode';

const componentName = 'CdsTreeNode';
export default defineComponent({
  componentName,
  inheritAttrs: false,
  isTreeNode: 1,
  props: {
    ...makeTreeNodeProps()
  },
  setup(props, { attrs, slots, expose }) {
    const dragNodeHighlight = ref(false);
    const context = useInjectTreeContext();
    const { expandedKeysSet, selectedKeysSet, loadedKeysSet, loadingKeysSet, checkedKeysSet, halfCheckedKeysSet } =
      useInjectKeysState();

    const { dragOverNodeKey, dropPosition, keyEntities } = unref(context);

    const mergedTreeNodeProps = computed(() => {
      return getTreeNodeProps(props.eventKey, {
        expandedKeysSet: unref(expandedKeysSet),
        selectedKeysSet: unref(selectedKeysSet),
        loadedKeysSet: unref(loadedKeysSet),
        loadingKeysSet: unref(loadingKeysSet),
        checkedKeysSet: unref(checkedKeysSet),
        halfCheckedKeysSet: unref(halfCheckedKeysSet),
        dragOverNodeKey,
        dropPosition,
        keyEntities
      });
    });

    const expanded = eagerComputed(() => unref(mergedTreeNodeProps).expanded);
    const selected = eagerComputed(() => unref(mergedTreeNodeProps).selected);
    const checked = eagerComputed(() => unref(mergedTreeNodeProps).checked);
    const loaded = eagerComputed(() => unref(mergedTreeNodeProps).loaded);
    const loading = eagerComputed(() => unref(mergedTreeNodeProps).loading);
    const halfChecked = eagerComputed(() => unref(mergedTreeNodeProps).halfChecked);
    const dragOver = eagerComputed(() => unref(mergedTreeNodeProps).dragOver);
    const dragOverGapTop = eagerComputed(() => unref(mergedTreeNodeProps).dragOverGapTop);
    const dragOverGapBottom = eagerComputed(() => unref(mergedTreeNodeProps).dragOverGapBottom);
    const pos = eagerComputed(() => unref(mergedTreeNodeProps).pos);

    const selectHandle = ref();

    const hasChildren = computed(() => {
      const { eventKey } = props;
      const { keyEntities } = unref(context);
      const { children } = keyEntities[eventKey] || {};

      return !!(children || []).length;
    });

    const isLeaf = computed(() => {
      const { isLeaf } = props;
      const { loadData } = unref(context);

      const has = unref(hasChildren);

      if (isLeaf === false) {
        return false;
      }

      return isLeaf || (!loadData && !has) || (loadData && loaded.value && !has);
    });

    const nodeState = computed(() => {
      if (unref(isLeaf)) {
        return null;
      }

      return unref(expanded) ? ICON_OPEN : ICON_CLOSE;
    });

    const isDisabled = computed(() => {
      const { disabled } = props;
      const { disabled: treeDisabled } = unref(context);

      return !!(treeDisabled || disabled);
    });

    const isCheckable = computed(() => {
      const { checkable } = props;
      const { checkable: treeCheckable } = unref(context);

      // Return false if tree or treeNode is not checkable
      if (!treeCheckable || checkable === false) return false;
      return treeCheckable;
    });

    const isSelectable = computed(() => {
      const { selectable } = props;
      const { selectable: treeSelectable } = unref(context);

      // Ignore when selectable is undefined or null
      if (typeof selectable === 'boolean') {
        return selectable;
      }

      return treeSelectable;
    });

    const renderArgsData = computed(() => {
      const { data, active, checkable, disableCheckbox, disabled, selectable } = props;
      return {
        active,
        checkable,
        disableCheckbox,
        disabled,
        selectable,
        ...data,
        dataRef: data,
        data,
        isLeaf: unref(isLeaf),
        checked: unref(checked),
        expanded: unref(expanded),
        loading: unref(loading),
        selected: unref(selected),
        halfChecked: unref(halfChecked)
      };
    });

    const instance = getCurrentInstance('CdsTreeNode');

    const eventData = computed(() => {
      const { eventKey } = props;
      const { keyEntities } = unref(context);
      const { parent } = keyEntities[eventKey] || {};
      return {
        ...convertNodePropsToEventData(Object.assign({}, props, unref(mergedTreeNodeProps))),
        parent
      };
    });

    const dragNodeEvent: NTree.TDragNodeEvent = reactive({
      eventData,
      eventKey: computed(() => props.eventKey),
      selectHandle,
      pos,
      key: instance.vnode.key as NTree.TKey
    });

    expose(dragNodeEvent);

    const onSelectorDoubleClick = (event: MouseEvent) => {
      const { onNodeDoubleClick } = unref(context);
      onNodeDoubleClick(event, unref(eventData));
    };

    const onSelect = (event: MouseEvent) => {
      if (unref(isDisabled)) return;

      const { onNodeSelect } = unref(context);
      event.preventDefault();
      onNodeSelect(event, unref(eventData));
    };

    const onCheck = (event: MouseEvent) => {
      if (unref(isDisabled)) return;

      const { disableCheckbox } = props;
      const { onNodeCheck } = unref(context);

      if (!unref(isCheckable) || disableCheckbox) return;

      // event.preventDefault();

      const targetChecked = !unref(checked);
      onNodeCheck(event, unref(eventData), targetChecked);
    };

    const onSelectorClick = (event: MouseEvent) => {
      // Click trigger before select/check operation
      const { onNodeClick } = unref(context);
      onNodeClick(event, unref(eventData));

      if (unref(isSelectable)) {
        onSelect(event);
      } else {
        onCheck(event);
      }
    };

    const onMouseEnter = (event: MouseEvent) => {
      const { onNodeMouseEnter } = unref(context);
      onNodeMouseEnter(event, unref(eventData));
    };

    const onMouseLeave = (event: MouseEvent) => {
      const { onNodeMouseLeave } = unref(context);
      onNodeMouseLeave(event, unref(eventData));
    };

    const onContextmenu = (event: MouseEvent) => {
      const { onNodeContextMenu } = unref(context);
      onNodeContextMenu(event, unref(eventData));
    };

    const onDragStart = (event: DragEvent) => {
      const { onNodeDragStart } = unref(context);

      event.stopPropagation();

      dragNodeHighlight.value = true;

      onNodeDragStart(event, dragNodeEvent);

      try {
        // ie throw error
        // firefox-need-it
        event.dataTransfer!.setData('text/plain', '');
      } catch (error) {
        // empty
      }
    };

    const onDragEnter = (event: DragEvent) => {
      const { onNodeDragEnter } = unref(context);

      event.preventDefault();
      event.stopPropagation();
      onNodeDragEnter(event, dragNodeEvent);
    };

    const onDragOver = (event: DragEvent) => {
      const { onNodeDragOver } = unref(context);

      event.preventDefault();
      event.stopPropagation();
      onNodeDragOver(event, dragNodeEvent);
    };

    const onDragLeave = (event: DragEvent) => {
      const { onNodeDragLeave } = unref(context);

      event.stopPropagation();
      onNodeDragLeave(event, dragNodeEvent);
    };

    const onDragEnd = (event: DragEvent) => {
      const { onNodeDragEnd } = unref(context);

      event.stopPropagation();
      dragNodeHighlight.value = false;
      onNodeDragEnd(event, dragNodeEvent);
    };

    const onDrop = (event: DragEvent) => {
      const { onNodeDrop } = unref(context);

      event.preventDefault();
      event.stopPropagation();
      dragNodeHighlight.value = false;
      onNodeDrop(event, dragNodeEvent);
    };

    // Disabled item still can be switch
    const onExpand = (event: MouseEvent) => {
      const { onNodeExpand } = unref(context);
      if (unref(loading)) return;
      onNodeExpand(event, unref(eventData));
    };

    const isDraggable = () => {
      const { data } = props;
      const { draggable } = unref(context);
      return draggable && (!draggable.nodeDraggable || draggable.nodeDraggable(data));
    };

    // ==================== Render: Drag Handler ====================
    const renderDragHandler = () => {
      const { draggable } = unref(context);
      return draggable && draggable?.icon
        ? h(CdsIcon, {
            name: typeof draggable.icon === 'string' ? draggable.icon : 'draggable',
            class: 'cds-treenode__draggable-icon'
          })
        : null;
    };

    const renderSwitcherIconDom = () => {
      const {
        switcherIcon: switcherIconFromProps = slots.switcherIcon ||
          unref(context).slots?.[props.data?.slots?.switcherIcon!]
      } = props;
      const { switcherIcon: switcherIconFromCtx } = unref(context);

      const switcherIcon = switcherIconFromProps || switcherIconFromCtx;
      // if switcherIconDom is null, no render switcher span
      if (typeof switcherIcon === 'function') {
        return switcherIcon(unref(renderArgsData));
      }

      return switcherIcon;
    };

    const renderSwitcher = () => {
      const switcherIconDom = renderSwitcherIconDom();

      if (unref(isLeaf)) {
        return switcherIconDom !== false
          ? h('span', { class: classNames(`${prefixCls}__switcher`, `${prefixCls}__switcher--noop`) }, switcherIconDom)
          : null;
      }

      const switcherCls = classNames(
        `${prefixCls}__switcher`,
        `${prefixCls}__switcher--${unref(expanded) ? ICON_OPEN : ICON_CLOSE}`
      );

      return switcherIconDom !== false ? h('span', { class: switcherCls, onClick: onExpand }, switcherIconDom) : null;
    };

    const renderCheckbox = () => {
      if (!unref(isCheckable)) return null;

      return h(
        'span',
        { class: classNames('cds-treenode__checkbox') },
        h(CdsCheckbox, {
          modelValue: unref(checked),
          indeterminate: !unref(checked) && unref(halfChecked),
          disabled: unref(isDisabled) || props.disableCheckbox,
          hideMessages: true,
          'onUpdate:modelValue': onCheck
        })
      );
    };

    const renderIcon = () => {
      return h('span', {
        class: classNames(
          `${prefixCls}__icon`,
          `${prefixCls}__icon--${unref(nodeState) || 'docu'}`,
          unref(loading) && `${prefixCls}__icon--loading`
        )
      });
    };

    const renderDropIndicator = () => {
      const { disabled, eventKey } = props;
      const { draggable, dropLevelOffset, dropPosition, indent, dropIndicatorRender, dragOverNodeKey } = unref(context);
      const rootDraggable = draggable !== false;
      // allowDrop is calculated in Tree.tsx, there is no need for calc it here
      const showIndicator = !disabled && rootDraggable && dragOverNodeKey === eventKey;
      return showIndicator ? dropIndicatorRender({ dropPosition, dropLevelOffset, indent, prefixCls }) : null;
    };

    const renderSelector = () => {
      const { icon = slots.icon, data } = props;
      const title =
        slots.title || unref(context).slots?.[props.data?.slots?.title!] || unref(context).slots?.title || props.title;
      const { showIcon, icon: treeIcon, loadData } = unref(context);

      const wrapperClass = `${prefixCls}__node-content-wrapper`;

      let $icon;

      if (showIcon) {
        const currentIcon = icon || unref(context).slots?.[data?.slots?.icon!] || treeIcon;

        $icon = currentIcon
          ? h(
              'span',
              { className: classNames(`${prefixCls}__icon`, `${prefixCls}__icon-customize`) },
              typeof currentIcon === 'function' ? currentIcon(unref(renderArgsData)) : currentIcon
            )
          : renderIcon();
      }

      // Title
      let titleNode: any;
      if (typeof title === 'function') {
        titleNode = title(unref(renderArgsData));
      } else {
        titleNode = title;
      }

      titleNode = titleNode || defaultTitle;

      const $title = h('span', { class: `${prefixCls}__title` }, titleNode);

      return h(
        'span',
        {
          ref: selectHandle,
          title: typeof title === 'string' ? title : '',
          class: classNames(
            wrapperClass,
            `${wrapperClass}--${unref(nodeState) || 'normal'}`,
            unref(isDisabled) && (unref(selected) || unref(dragNodeHighlight)) && `${prefixCls}--node-selected`
          ),
          tabindex: unref(isSelectable) ? 0 : undefined,
          onMouseenter: onMouseEnter,
          onMouseleave: onMouseLeave,
          onContextmenu: onContextmenu,
          onDblclick: onSelectorDoubleClick,
          onClick: onSelectorClick
        },
        [$icon, $title, renderDropIndicator() as VNode]
      );
    };

    // Load data to avoid default expanded tree without data
    const syncLoadData = () => {
      const { loadData, onNodeLoad } = unref(context);

      if (unref(loading)) return;

      // read from state to avoid loadData at same time
      if (loadData && expanded.value && !isLeaf.value) {
        // We needn't reload data when has children in sync logic
        // It's only needed in node expanded
        if (!hasChildren.value && !loaded.value) {
          onNodeLoad(eventData.value);
        }
      }
    };

    onMounted(() => {
      syncLoadData();
    });
    onUpdated(() => {
      syncLoadData();
    });

    useRender(() => {
      const { eventKey, isLeaf, isStart, isEnd, domRef, active, data, onMousemove, selectable, ...otherProps } = {
        ...props,
        ...attrs
      };
      const { filterTreeNode, keyEntities, dropContainerKey, dropTargetKey, draggingNodeKey } = unref(context);
      const dataOrAriaAttributeProps = pickAttrs(otherProps, { aria: true, data: true });

      const { level } = keyEntities[eventKey] || {};
      const isEndNode = isEnd![isEnd!.length - 1];

      const mergedDraggable = isDraggable();
      const draggableWithoutDisabled = !unref(isDisabled) && mergedDraggable;

      const dragging = draggingNodeKey === eventKey;
      const ariaSelected = selectable !== undefined ? { 'aria-selected': !!selectable } : undefined;

      const prefixCls = 'cds-treenode';

      return h(
        'div',
        {
          ref: domRef,
          class: classNames(attrs.class, 'cds-treenode', {
            [`${prefixCls}__switcher--${unref(expanded) ? 'open' : 'close'}`]: !isLeaf,
            [`${prefixCls}__checkbox--checked`]: unref(checked),
            [`${prefixCls}__checkbox--indeterminate`]: unref(halfChecked),
            [`${prefixCls}--disabled`]: unref(isDisabled),
            [`${prefixCls}--selected`]: unref(selected),
            [`${prefixCls}--loading`]: unref(loading),
            [`${prefixCls}--active`]: active,
            [`${prefixCls}--leaf-last`]: isEndNode,
            [`${prefixCls}--draggable`]: draggableWithoutDisabled,

            dragging,
            'drop-target': dropTargetKey === eventKey,
            'drop-container': dropContainerKey === eventKey,
            'drag-over': !unref(isDisabled) && unref(dragOver),
            'drag-over-gap-top': !unref(isDisabled) && unref(dragOverGapTop),
            'drag-over-gap-bottom': !unref(isDisabled) && unref(dragOverGapBottom),
            'filter-node': filterTreeNode && filterTreeNode(unref(eventData))
          }),
          style: attrs.style as CSSProperties,
          draggable: draggableWithoutDisabled,
          'aria-grabbed': dragging ? true : undefined,
          onDragstart: draggableWithoutDisabled ? onDragStart : undefined,
          onDragenter: mergedDraggable ? onDragEnter : undefined,
          onDragover: mergedDraggable ? onDragOver : undefined,
          onDragleave: mergedDraggable ? onDragLeave : undefined,
          onDragend: mergedDraggable ? onDragEnd : undefined,
          onDrop: mergedDraggable ? onDrop : undefined,
          onMousemove,
          ...ariaSelected,
          ...dataOrAriaAttributeProps
        },
        [
          renderIndent({ prefixCls, level, isStart, isEnd }),
          renderDragHandler(),
          renderSwitcher(),
          renderCheckbox(),
          renderSelector()
        ]
      );
    });
  }
});
</script>
